import {
  Alert,
  Avatar,
  Button,
  Card,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
} from '@/components/ui';
import { StandardIcon } from '@/components/common/icon';
import { useApiWithAuth } from '@/hooks/api';
import { useDebounce } from '@/hooks/debounce';
import type {
  LinkMigrationProfileResponse,
  MigrationMhProfile,
  MigrationPcoCandidate,
  MigrationPcoOnlyProfile,
  ProfileMigrationIssue,
  ProfileMigrationListResponse,
  ProfileMigrationView,
} from '@/shared/types/profileMigration';
import { useEffect, useMemo, useRef, useState } from 'react';

const PAGE_SIZE = 25;

const issueContent: Record<
  ProfileMigrationIssue,
  { label: string; className: string; icon: string }
> = {
  linked: {
    label: 'Linked',
    className: 'bg-emerald-50 text-emerald-700',
    icon: 'mdi:link-variant',
  },
  orphaned_mh: {
    label: 'No PCO link',
    className: 'bg-amber-50 text-amber-800',
    icon: 'mdi:link-variant-off',
  },
  ambiguous_mh: {
    label: 'Multiple PCO links',
    className: 'bg-rose-50 text-rose-700',
    icon: 'mdi:source-branch',
  },
  ambiguous_pco: {
    label: 'PCO claimed more than once',
    className: 'bg-violet-50 text-violet-700',
    icon: 'mdi:account-multiple-outline',
  },
  missing_pco: {
    label: 'PCO profile missing',
    className: 'bg-slate-100 text-slate-700',
    icon: 'mdi:account-question-outline',
  },
};

function IssueChip({ issue }: { issue: ProfileMigrationIssue }) {
  const content = issueContent[issue];

  return (
    <Chip className={`${content.className} rounded-md px-2 py-1 font-medium`}>
      <span className='inline-flex items-center gap-1.5'>
        <StandardIcon id={content.icon} size={14} />
        {content.label}
      </span>
    </Chip>
  );
}

function initials(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function IdentityBlock({
  source,
  name,
  id,
  email,
  phone,
  birthdate,
  status,
}: {
  source: 'MH' | 'PCO';
  name: string;
  id: string;
  email: string;
  phone: string;
  birthdate: string;
  status?: string;
}) {
  return (
    <div className='min-w-0'>
      <div className='flex min-w-0 items-center gap-3'>
        <Avatar
          name={name || id}
          getInitials={() => initials(name || source)}
          size='sm'
          className={
            source === 'MH'
              ? 'bg-sky-100 text-sky-700'
              : 'bg-indigo-100 text-indigo-700'
          }
        />
        <div className='min-w-0'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='truncate font-semibold text-slate-900'>
              {name || `${source} profile ${id}`}
            </span>
            <span className='rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-slate-500'>
              {source}
            </span>
            {status ? (
              <span className='text-[11px] font-medium uppercase text-slate-500'>
                {status}
              </span>
            ) : null}
          </div>
          <div className='mt-0.5 truncate text-xs text-slate-400'>{id}</div>
        </div>
      </div>
      <div className='mt-3 grid gap-1 text-xs text-slate-600 sm:grid-cols-2'>
        <span className='truncate'>{email || 'No email'}</span>
        <span>{phone || 'No phone'}</span>
        <span>{birthdate || 'No birthdate'}</span>
      </div>
    </div>
  );
}

function EngagementCounts({ profile }: { profile: MigrationMhProfile }) {
  const counts = [
    {
      label: 'Comments',
      value: profile.engagement.comments,
      icon: 'mdi:comment-text-outline',
    },
    {
      label: 'Reminders',
      value: profile.engagement.reminders,
      icon: 'mdi:bell-outline',
    },
    {
      label: 'Milestones',
      value: profile.engagement.milestones,
      icon: 'mdi:flag-outline',
    },
    {
      label: 'Mentions',
      value: profile.engagement.mentions,
      icon: 'mdi:at',
    },
  ];

  return (
    <div className='flex flex-wrap gap-2' aria-label='Linked engagement counts'>
      {counts.map((count) => (
        <span
          key={count.label}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
            count.value
              ? 'border-blue-100 bg-blue-50 text-blue-700'
              : 'border-slate-100 bg-slate-50 text-slate-400'
          }`}
          title={count.label}
        >
          <StandardIcon id={count.icon} size={14} />
          <span className='font-semibold'>{count.value}</span>
          <span className='hidden lg:inline'>{count.label}</span>
        </span>
      ))}
    </div>
  );
}

function Evidence({ candidate }: { candidate: MigrationPcoCandidate }) {
  const label: Record<string, string> = {
    email: 'Same email',
    phone: 'Same phone',
    birthdate: 'Same birthdate',
    first_last: 'Same name',
    preferred_or_nickname_last: 'Preferred name',
    normalized_fullname: 'Same name',
    gender: 'Same gender',
    existing_link: 'Existing link',
  };

  return (
    <div className='flex flex-wrap gap-1'>
      {candidate.evidence.slice(0, 4).map((evidence) => (
        <Chip
          key={evidence}
          className='rounded-md bg-emerald-50 text-emerald-700'
        >
          {label[evidence] || evidence.replaceAll('_', ' ')}
        </Chip>
      ))}
      {candidate.conflicts.map((conflict) => (
        <Chip key={conflict} className='rounded-md bg-rose-50 text-rose-700'>
          {conflict.replaceAll('_', ' ')}
        </Chip>
      ))}
    </div>
  );
}

function CandidatePreview({
  candidate,
}: {
  candidate: MigrationPcoCandidate | undefined;
}) {
  if (!candidate) {
    return (
      <div className='rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-sm text-slate-500'>
        No likely PCO match found. Search PCO while reviewing this profile.
      </div>
    );
  }

  return (
    <div className='rounded-lg border border-slate-200 bg-slate-50 p-3'>
      <IdentityBlock
        source='PCO'
        name={candidate.name}
        id={candidate.pcoId}
        email={candidate.email}
        phone={candidate.phone}
        birthdate={candidate.birthdate}
        status={candidate.status}
      />
      <div className='mt-3 flex flex-wrap items-center justify-between gap-2'>
        <Evidence candidate={candidate} />
        <span
          className={`text-xs font-semibold ${
            candidate.confidence === 'strong'
              ? 'text-emerald-700'
              : 'text-slate-500'
          }`}
        >
          {candidate.confidence === 'strong'
            ? 'Strong candidate'
            : `Match score ${candidate.score}`}
        </span>
      </div>
    </div>
  );
}

function MigrationProfileCard({
  profile,
  onReview,
}: {
  profile: MigrationMhProfile;
  onReview: (profile: MigrationMhProfile) => void;
}) {
  const currentCandidate =
    profile.candidates.find((candidate) =>
      profile.existingPcoIds.includes(candidate.pcoId),
    ) || profile.candidates[0];

  return (
    <Card className='overflow-hidden shadow-none'>
      <div className='flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-white px-4 py-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <IssueChip issue={profile.issue} />
          {profile.archived ? (
            <Chip className='rounded-md bg-slate-100 text-slate-500'>
              Archived MH profile
            </Chip>
          ) : null}
          {profile.memberStatus ? (
            <span className='text-xs text-slate-500'>
              {profile.memberStatus}
            </span>
          ) : null}
        </div>
        <EngagementCounts profile={profile} />
      </div>
      <div className='grid gap-4 p-4 lg:grid-cols-[minmax(0,1fr)_32px_minmax(0,1fr)_auto] lg:items-center'>
        <IdentityBlock
          source='MH'
          name={profile.name}
          id={profile.mhxId}
          email={profile.email}
          phone={profile.phone}
          birthdate={profile.birthdate}
        />
        <div className='hidden justify-center text-slate-300 lg:flex'>
          <StandardIcon
            id={
              profile.issue === 'linked'
                ? 'mdi:link-variant'
                : 'mdi:arrow-right'
            }
            size={22}
          />
        </div>
        <CandidatePreview candidate={currentCandidate} />
        <Button
          color={profile.issue === 'linked' ? 'default' : 'primary'}
          variant={profile.issue === 'linked' ? 'flat' : 'solid'}
          onPress={() => onReview(profile)}
          className='w-full lg:w-auto'
          startContent={
            <StandardIcon
              id={
                profile.issue === 'linked'
                  ? 'mdi:pencil-outline'
                  : 'mdi:link-plus'
              }
              size={18}
            />
          }
        >
          {profile.issue === 'linked' ? 'Change link' : 'Review & link'}
        </Button>
      </div>
    </Card>
  );
}

function PcoOnlyCard({ profile }: { profile: MigrationPcoOnlyProfile }) {
  return (
    <Card className='p-4 shadow-none'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <IdentityBlock
          source='PCO'
          name={profile.name}
          id={profile.pcoId}
          email={profile.email}
          phone={profile.phone}
          birthdate={profile.birthdate}
          status={profile.status}
        />
        <Chip className='rounded-md bg-indigo-50 text-indigo-700'>
          Available PCO profile
        </Chip>
      </div>
    </Card>
  );
}

function CandidateOption({
  candidate,
  selected,
  onSelect,
}: {
  candidate: MigrationPcoCandidate;
  selected: boolean;
  onSelect: () => void;
}) {
  const claimedElsewhere = candidate.claimedMhxIds.length > 0;

  return (
    <button
      type='button'
      onClick={onSelect}
      className={`w-full rounded-lg border p-3 text-left transition ${
        selected
          ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <div className='truncate font-semibold text-slate-900'>
            {candidate.name || `PCO profile ${candidate.pcoId}`}
          </div>
          <div className='mt-0.5 text-xs text-slate-500'>
            {candidate.pcoId} · {candidate.email || 'No email'} ·{' '}
            {candidate.phone || 'No phone'}
          </div>
        </div>
        <div
          className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border ${
            selected ? 'border-4 border-blue-600 bg-white' : 'border-slate-300'
          }`}
        />
      </div>
      <div className='mt-2'>
        <Evidence candidate={candidate} />
      </div>
      {claimedElsewhere ? (
        <div className='mt-2 text-xs font-medium text-rose-700'>
          Already claimed by MH {candidate.claimedMhxIds.join(', ')}
        </div>
      ) : null}
    </button>
  );
}

function LinkProfileModal({
  profile,
  onClose,
  onLinked,
}: {
  profile: MigrationMhProfile | null;
  onClose: () => void;
  onLinked: (result: LinkMigrationProfileResponse) => void;
}) {
  const { invoke } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [candidates, setCandidates] = useState<MigrationPcoCandidate[]>([]);
  const [selectedPcoId, setSelectedPcoId] = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!profile) {
      return;
    }

    setSearchInput('');
    setCandidates(profile.candidates);
    setSelectedPcoId(
      profile.existingPcoIds[0] || profile.candidates[0]?.pcoId || '',
    );
    setError('');
  }, [profile]);

  useEffect(() => {
    if (!profile || debouncedSearch.trim().length < 2) {
      if (!debouncedSearch.trim()) {
        setCandidates(profile?.candidates || []);
      }

      return () => undefined;
    }

    let cancelled = false;
    const load = async () => {
      setSearching(true);
      setError('');
      try {
        const params = new URLSearchParams({
          search: debouncedSearch.trim(),
          mhxId: profile.mhxId,
          limit: '20',
        });
        const response = await invokeRef.current<{
          items?: MigrationPcoCandidate[];
          message?: string;
        }>(`/migration/pco-people?${params.toString()}`);
        if (!cancelled) {
          if (!response.items) {
            throw new Error(response.message || 'Unable to search PCO');
          }
          setCandidates(response.items);
        }
      } catch (searchError) {
        if (!cancelled) {
          setError(
            searchError instanceof Error
              ? searchError.message
              : 'Unable to search PCO',
          );
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, profile]);

  const selectedCandidate = candidates.find(
    (candidate) => candidate.pcoId === selectedPcoId,
  );
  const replacing = Boolean(
    profile?.existingPcoIds.some((pcoId) => pcoId !== selectedPcoId),
  );
  const selectedClaimedElsewhere = Boolean(
    selectedCandidate?.claimedMhxIds.length,
  );
  let candidateListContent;
  if (searching) {
    candidateListContent = [0, 1, 2].map((item) => (
      <Skeleton key={item} className='h-24 rounded-lg' />
    ));
  } else if (candidates.length) {
    candidateListContent = candidates.map((candidate) => (
      <CandidateOption
        key={candidate.pcoId}
        candidate={candidate}
        selected={candidate.pcoId === selectedPcoId}
        onSelect={() => setSelectedPcoId(candidate.pcoId)}
      />
    ));
  } else {
    candidateListContent = (
      <div className='rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500'>
        No PCO people found. Try a broader search.
      </div>
    );
  }

  const link = async () => {
    if (!profile || !selectedPcoId) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await invokeRef.current<
        LinkMigrationProfileResponse & { message?: string }
      >(`/migration/profiles/${profile.mhxId}/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pcoId: selectedPcoId,
          replaceExisting: replacing,
        }),
      });
      if (!response.mhxId) {
        throw new Error(response.message || 'Unable to link profiles');
      }
      onLinked(response);
    } catch (linkError) {
      setError(
        linkError instanceof Error
          ? linkError.message
          : 'Unable to link profiles',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={Boolean(profile)} onClose={onClose} placement='top'>
      <ModalContent size='2xl'>
        <ModalHeader>
          <div>
            <div>Link MH profile to PCO</div>
            <div className='mt-1 text-sm font-normal text-slate-500'>
              Choose the one PCO person who should own this profile’s
              engagement.
            </div>
          </div>
        </ModalHeader>
        <ModalBody className='space-y-5'>
          {profile ? (
            <div className='rounded-lg border border-sky-100 bg-sky-50 p-3'>
              <IdentityBlock
                source='MH'
                name={profile.name}
                id={profile.mhxId}
                email={profile.email}
                phone={profile.phone}
                birthdate={profile.birthdate}
              />
              <div className='mt-3'>
                <EngagementCounts profile={profile} />
              </div>
            </div>
          ) : null}

          <Input
            aria-label='Search PCO people'
            placeholder='Search PCO by name, email, phone, or ID'
            value={searchInput}
            onValueChange={setSearchInput}
            isClearable
            startContent={
              <StandardIcon
                id='mdi:magnify'
                size={18}
                className='text-slate-400'
              />
            }
          />

          {error ? <Alert color='danger'>{error}</Alert> : null}
          {replacing ? (
            <Alert color='warning' title='This replaces an existing link'>
              The old PCO assignment will be removed after you confirm the new
              profile.
            </Alert>
          ) : null}
          {selectedClaimedElsewhere ? (
            <Alert color='danger' title='This PCO profile is already claimed'>
              Resolve the duplicate MH profile before using this PCO person.
            </Alert>
          ) : null}

          <div>
            <div className='mb-2 flex items-center justify-between'>
              <h3 className='text-sm font-semibold text-slate-900'>
                {searchInput ? 'PCO search results' : 'Suggested PCO profiles'}
              </h3>
              {searching ? (
                <span className='text-xs text-slate-500'>Searching…</span>
              ) : null}
            </div>
            <div className='max-h-[22rem] space-y-2 overflow-y-auto pr-1'>
              {candidateListContent}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='flat' onPress={onClose}>
            Cancel
          </Button>
          <Button
            color='primary'
            onPress={() => void link()}
            isLoading={saving}
            isDisabled={!selectedPcoId || selectedClaimedElsewhere}
            startContent={<StandardIcon id='mdi:link-plus' size={18} />}
          >
            {replacing ? 'Replace link' : 'Link profiles'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function LoadingCards() {
  return (
    <div className='space-y-3'>
      {[0, 1, 2, 3].map((item) => (
        <Card key={item} className='p-4 shadow-none'>
          <div className='flex items-center justify-between gap-4'>
            <div className='flex flex-1 items-center gap-3'>
              <Skeleton className='h-10 w-10 shrink-0 rounded-full' />
              <div className='w-full space-y-2'>
                <Skeleton className='h-4 w-40 rounded' />
                <Skeleton className='h-3 max-w-xl rounded' />
              </div>
            </div>
            <Skeleton className='h-9 w-28 rounded-md' />
          </div>
        </Card>
      ))}
    </div>
  );
}

export function ProfileMigration() {
  const { invoke, auth } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;
  const [view, setView] = useState<ProfileMigrationView>('needs_review');
  const [issue, setIssue] = useState<ProfileMigrationIssue | ''>('');
  const [engagementOnly, setEngagementOnly] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 350);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ProfileMigrationListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewProfile, setReviewProfile] = useState<MigrationMhProfile | null>(
    null,
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const forceRefreshRef = useRef(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setPage(1);
  }, [view, issue, engagementOnly, debouncedSearch]);

  useEffect(() => {
    if (!auth.firebaseUser?.uid) {
      return () => undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          view,
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (issue && view !== 'pco_only') {
          params.set('issue', issue);
        }
        if (engagementOnly && view !== 'pco_only') {
          params.set('engagement', 'true');
        }
        if (debouncedSearch.trim()) {
          params.set('search', debouncedSearch.trim());
        }
        if (forceRefreshRef.current) {
          params.set('refresh', 'true');
        }
        const response = await invokeRef.current<
          ProfileMigrationListResponse & {
            message?: string;
          }
        >(`/migration/profiles?${params.toString()}`);
        if (!response.summary) {
          throw new Error(
            response.message || 'Unable to load profile migration data',
          );
        }
        if (!cancelled) {
          setData(response);
          forceRefreshRef.current = false;
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : 'Unable to load profile migration data',
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    void load();

    return () => {
      cancelled = true;
    };
  }, [
    auth.firebaseUser?.uid,
    view,
    issue,
    engagementOnly,
    debouncedSearch,
    page,
    refreshKey,
  ]);

  const summary = data?.summary;
  const progress =
    summary?.mhTotal && summary.mhTotal > 0
      ? Math.round((summary.linkedMh / summary.mhTotal) * 100)
      : 0;
  const totalPages = Math.max(1, Math.ceil((data?.count || 0) / PAGE_SIZE));
  const tabs = useMemo(
    () => [
      {
        key: 'needs_review' as const,
        label: 'Needs review',
        count: summary ? summary.mhTotal - summary.linkedMh : undefined,
      },
      {
        key: 'linked' as const,
        label: 'Linked',
        count: summary?.linkedMh,
      },
      {
        key: 'pco_only' as const,
        label: 'PCO only',
        count: summary?.pcoOnly,
      },
      {
        key: 'all' as const,
        label: 'All MH profiles',
        count: summary?.mhTotal,
      },
    ],
    [summary],
  );
  let profileListContent;
  if (loading && !data) {
    profileListContent = <LoadingCards />;
  } else if (data?.items.length) {
    profileListContent = (
      <div className='space-y-3'>
        {data.items.map((item) =>
          item.kind === 'mh' ? (
            <MigrationProfileCard
              key={item.mhxId}
              profile={item}
              onReview={setReviewProfile}
            />
          ) : (
            <PcoOnlyCard key={item.pcoId} profile={item} />
          ),
        )}
      </div>
    );
  } else {
    profileListContent = (
      <Card className='p-10 text-center shadow-none'>
        <StandardIcon
          id='mdi:account-search-outline'
          size={34}
          className='mx-auto text-slate-300'
        />
        <h2 className='mt-3 font-semibold text-slate-900'>
          No profiles match this view
        </h2>
        <p className='mt-1 text-sm text-slate-500'>
          Try clearing the search or engagement filter.
        </p>
      </Card>
    );
  }

  return (
    <div className='mx-auto w-full max-w-[1600px] space-y-5 px-4 pb-8 pt-2 sm:px-6'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <div className='mb-1 flex items-center gap-2 text-sm font-semibold text-blue-700'>
            <StandardIcon id='mdi:swap-horizontal-bold' size={18} />
            Identity migration
          </div>
          <h1 className='text-2xl font-semibold tracking-tight text-slate-950'>
            Link every MH profile to the right PCO person
          </h1>
          <p className='mt-1 max-w-3xl text-sm text-slate-600'>
            Resolve orphaned and ambiguous identities before comments,
            reminders, milestones, and mentions move to PCO-backed profiles.
          </p>
        </div>
        <Button
          variant='flat'
          onPress={() => {
            forceRefreshRef.current = true;
            setRefreshKey((value) => value + 1);
          }}
          isLoading={loading && Boolean(data)}
          startContent={<StandardIcon id='mdi:refresh' size={18} />}
        >
          Refresh data
        </Button>
      </div>

      {error ? <Alert color='danger'>{error}</Alert> : null}
      {success ? (
        <Alert color='success' title='Profiles linked'>
          {success}
        </Alert>
      ) : null}
      {summary?.warnings.length ? (
        <Alert color='warning' title='Some counts are incomplete'>
          {summary.warnings.join(' · ')}
        </Alert>
      ) : null}

      <Card className='overflow-hidden shadow-none'>
        <div className='grid gap-0 xl:grid-cols-[1.35fr_repeat(4,minmax(0,1fr))]'>
          <div className='border-b border-slate-100 p-5 xl:border-b-0 xl:border-r'>
            <div className='flex items-end justify-between gap-4'>
              <div>
                <div className='text-sm font-medium text-slate-600'>
                  MH profiles linked
                </div>
                <div className='mt-1 text-3xl font-semibold text-slate-950'>
                  {summary ? `${progress}%` : '—'}
                </div>
              </div>
              <div className='text-right text-sm text-slate-500'>
                {summary
                  ? `${summary.linkedMh.toLocaleString()} of ${summary.mhTotal.toLocaleString()}`
                  : 'Loading current links'}
              </div>
            </div>
            <div className='mt-4 h-2 overflow-hidden rounded-full bg-slate-100'>
              <div
                className='h-full rounded-full bg-blue-600 transition-all'
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
          {[
            {
              label: 'Orphaned MH',
              value: summary?.orphanedMh,
              icon: 'mdi:link-variant-off',
              className: 'text-amber-700 bg-amber-50',
            },
            {
              label: 'Ambiguous',
              value: summary
                ? summary.ambiguousMh + summary.ambiguousPco
                : undefined,
              icon: 'mdi:source-branch',
              className: 'text-rose-700 bg-rose-50',
            },
            {
              label: 'Comments',
              value: summary?.engagement.comments,
              icon: 'mdi:comment-text-outline',
              className: 'text-blue-700 bg-blue-50',
            },
            {
              label: 'Other items',
              value: summary
                ? summary.engagement.reminders +
                  summary.engagement.milestones +
                  summary.engagement.mentions
                : undefined,
              icon: 'mdi:database-outline',
              className: 'text-indigo-700 bg-indigo-50',
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className='flex items-center gap-3 border-b border-slate-100 p-5 last:border-b-0 xl:border-b-0 xl:border-r xl:last:border-r-0'
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${stat.className}`}
              >
                <StandardIcon id={stat.icon} size={20} />
              </span>
              <div>
                <div className='text-xl font-semibold text-slate-950'>
                  {stat.value === undefined ? '—' : stat.value.toLocaleString()}
                </div>
                <div className='text-xs text-slate-500'>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className='border-b border-slate-200'>
        <div className='flex gap-1 overflow-x-auto'>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type='button'
              onClick={() => {
                setView(tab.key);
                setIssue('');
              }}
              className={`flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition ${
                view === tab.key
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab.label}
              {tab.count !== undefined ? (
                <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600'>
                  {tab.count.toLocaleString()}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <Input
          aria-label='Search migration profiles'
          placeholder='Search name, email, phone, MH ID, or PCO ID'
          value={searchInput}
          onValueChange={setSearchInput}
          isClearable
          wrapperClassName='min-w-[18rem]'
          className='max-w-xl flex-1'
          startContent={
            <StandardIcon
              id='mdi:magnify'
              size={18}
              className='text-slate-400'
            />
          }
        />
        {view !== 'pco_only' && view !== 'linked' ? (
          <label className='flex items-center gap-2 text-sm text-slate-600'>
            <span className='sr-only'>Issue type</span>
            <select
              value={issue}
              onChange={(event) =>
                setIssue(event.target.value as ProfileMigrationIssue | '')
              }
              className='h-10 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none'
            >
              <option value=''>All issues</option>
              <option value='orphaned_mh'>No PCO link</option>
              <option value='ambiguous_mh'>Multiple PCO links</option>
              <option value='ambiguous_pco'>PCO claimed more than once</option>
              <option value='missing_pco'>PCO profile missing</option>
            </select>
          </label>
        ) : null}
        {view !== 'pco_only' ? (
          <label className='flex h-10 cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-600'>
            <input
              type='checkbox'
              checked={engagementOnly}
              onChange={(event) => setEngagementOnly(event.target.checked)}
              className='h-4 w-4 rounded border-slate-300 text-blue-600'
            />
            Has engagement
          </label>
        ) : null}
        <div className='ml-auto text-sm text-slate-500'>
          {data ? `${data.count.toLocaleString()} profiles` : ''}
        </div>
      </div>

      {profileListContent}

      {data && totalPages > 1 ? (
        <div className='flex items-center justify-between gap-3 border-t border-slate-100 pt-4'>
          <span className='text-sm text-slate-500'>
            Page {page} of {totalPages}
          </span>
          <div className='flex gap-2'>
            <Button
              variant='flat'
              isDisabled={page <= 1}
              onPress={() => setPage((value) => Math.max(1, value - 1))}
            >
              Previous
            </Button>
            <Button
              variant='flat'
              isDisabled={page >= totalPages}
              onPress={() =>
                setPage((value) => Math.min(totalPages, value + 1))
              }
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <LinkProfileModal
        profile={reviewProfile}
        onClose={() => setReviewProfile(null)}
        onLinked={(result) => {
          setReviewProfile(null);
          setSuccess(
            `MH ${result.mhxId} is now linked to PCO ${result.pcoId}.`,
          );
          setRefreshKey((value) => value + 1);
        }}
      />
    </div>
  );
}
